import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Bot, Mic, MicOff, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useCurrency } from '../services/CurrencyContext';
import { useCountry } from '../context/CountryContext';
import { API_BASE_URL } from '../config';

interface PropertyMini {
    id: number;
    title: string;
    price: number;
    image: string | null;
    location: string;
}

interface Message {
    id: string;
    text: string;
    sender: 'user' | 'bot';
    timestamp: Date;
    listings?: PropertyMini[];
}

const ChatWidget = () => {
    const { formatPrice } = useCurrency();
    const { i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([
        { id: '1', text: "Hello! Looking for a Villa in New Cairo? Ask me! 🏠", sender: 'bot', timestamp: new Date() }
    ]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const location = useLocation();
    const { activeCountry } = useCountry();
    const getPath = (path: string) => {
        if (path.startsWith('http') || path.startsWith('/admin')) return path;
        const prefix = activeCountry ? `/${activeCountry.code.toLowerCase()}` : '';
        if (path === '/') return prefix || '/';
        return `${prefix}${path.startsWith('/') ? path : '/' + path}`;
    };

    const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    useEffect(scrollToBottom, [messages, isOpen]);

    // --- Voice Logic (Preserved) ---
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef<any>(null);

    useEffect(() => {
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = false;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.lang = 'en-US';

            recognitionRef.current.onresult = (event: any) => {
                const transcript = event.results[0][0].transcript;
                setInput(transcript);
            };

            recognitionRef.current.onend = () => {
                setIsListening(false);
            };
        }
    }, []);

    const toggleListening = () => {
        if (isListening) {
            recognitionRef.current?.stop();
            setIsListening(false);
        } else {
            setInput('');
            recognitionRef.current?.start();
            setIsListening(true);
        }
    };
    // -------------------

    // Listen for global open command
    useEffect(() => {
        const handleOpen = () => setIsOpen(true);
        window.addEventListener('open-chat', handleOpen);
        return () => window.removeEventListener('open-chat', handleOpen);
    }, []);

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim()) return;

        const userMsg: Message = { id: Date.now().toString(), text: input, sender: 'user', timestamp: new Date() };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setIsTyping(true);

        const historyText = messages.slice(-6).map(m => `${m.sender.toUpperCase()}: ${m.text}`).join('\n');
        
        // Extract IDs from context
        let prevIds: number[] = [];
        for (let i = messages.length - 1; i >= 0; i--) {
             if (messages[i].sender === 'bot' && messages[i].listings && messages[i].listings!.length > 0) {
                 prevIds = messages[i].listings!.map(l => l.id);
                 break; 
             }
        }

        try {
            const res = await api.post('/api/v1/intelligence/chat', {
                message: userMsg.text,
                history: historyText,
                prev_property_ids: prevIds,
                page_context: location.pathname,
                language: i18n.language // Pass UI language
            });

            const botMsg: Message = { 
                id: (Date.now() + 1).toString(), 
                text: res.data.response, 
                sender: 'bot', 
                timestamp: new Date(),
                listings: res.data.listings
            };
            setMessages(prev => [...prev, botMsg]);
        } catch {
            setMessages(prev => [...prev, { id: 'err', text: "Connection error.", sender: 'bot', timestamp: new Date() }]);
        } finally {
            setIsTyping(false);
        }
    };

    return (
        <div className="fixed bottom-6 right-6 z-[100] font-sans">
            <AnimatePresence>
                {isOpen && (
                    <motion.div 
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.98 }}
                        transition={{ duration: 0.3, type: "spring", bounce: 0.2 }}
                        className="w-[90vw] md:w-[380px] h-[650px] rounded-[32px] overflow-hidden flex flex-col relative shadow-2xl bg-white dark:bg-[#111]"
                    >
                         {/* Header */}
                        <div className="relative z-10 px-6 pt-8 pb-2 flex items-center justify-between shrink-0 bg-transparent">
                             <div className="flex items-center gap-3">
                                 <div className="w-10 h-10 rounded-md bg-gray-50 dark:bg-white/5 flex items-center justify-center">
                                     <Bot className="w-5 h-5 text-black dark:text-white relative z-10" />
                                 </div>
                                 <div className="flex flex-col">
                                     <h3 className="font-black text-sm text-black dark:text-white leading-none uppercase tracking-widest mb-1">LAQY AI</h3>
                                     <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest leading-none flex items-center gap-1.5">
                                         <span className="w-1.5 h-1.5 rounded-full bg-emerald-800"></span>
                                         Online
                                     </p>
                                 </div>
                             </div>
                             <div className="flex items-center gap-2">
                                 <button 
                                     onClick={() => setIsOpen(false)} 
                                     className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 dark:text-white/40 hover:text-black dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5 transition-all"
                                 >
                                     <X className="w-4 h-4" />
                                 </button>
                             </div>
                        </div>

                        {/* Content */}
                        <div className="relative z-10 flex-grow overflow-y-auto px-6 space-y-6 scrollbar-thin scrollbar-thumb-gray-100 dark:scrollbar-thumb-white/5 scrollbar-track-transparent pb-6 pt-4">
                            {messages.map(msg => (
                                <div key={msg.id} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}>
                                    <div className={`
                                        max-w-[90%] p-5 text-[13px] font-medium leading-relaxed
                                        ${msg.sender === 'user' 
                                            ? 'bg-black dark:bg-white text-white dark:text-black rounded-md rounded-tr-sm shadow-xl' 
                                            : 'bg-gray-50 dark:bg-[#181818] text-gray-600 dark:text-gray-300 rounded-md rounded-tl-sm'
                                        }
                                    `}>
                                        <p className="relative z-10">{msg.text}</p>
                                    </div>

                                    {/* Listings Cards - Ultra Minimal */}
                                    {msg.listings && msg.listings.length > 0 && (
                                        <div className="mt-4 w-full space-y-3 pl-2">
                                            {msg.listings.map(prop => (
                                                <Link 
                                                    to={getPath(`/property/${prop.id}`)} 
                                                    key={prop.id} 
                                                    onClick={() => setIsOpen(false)} 
                                                    className="group flex items-center gap-4 bg-gray-50 dark:bg-[#181818] p-2 pr-4 rounded-md hover:bg-gray-100 dark:hover:bg-[#222] transition-colors"
                                                >
                                                    <div className="relative w-16 h-16 shrink-0 bg-gray-200 dark:bg-[#252525] rounded-md overflow-hidden">
                                                        {prop.image ? (
                                                            <img 
                                                                src={prop.image.startsWith('http') ? prop.image : `${API_BASE_URL}${prop.image}`} 
                                                                className="w-full h-full object-cover transition-all duration-500" 
                                                                alt="prop" 
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-white/20"><Bot className="w-5 h-5"/></div>
                                                        )}
                                                    </div>
                                                    <div className="min-w-0 flex-grow py-1">
                                                        <h4 className="font-bold text-xs text-black dark:text-white uppercase tracking-wide truncate group-hover:text-amber-600 transition-colors">{prop.title}</h4>
                                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate mt-1">{prop.location}</p>
                                                        <div className="flex items-center gap-1.5 mt-1.5">
                                                            <span className="text-[10px] font-black text-black dark:text-white uppercase tracking-tight">{formatPrice(prop.price)}</span>
                                                        </div>
                                                    </div>
                                                    <div className="">
                                                        <ArrowRight className="w-3.5 h-3.5 text-gray-300 dark:text-white/20 -rotate-45 group-hover:text-black dark:group-hover:text-white transition-colors" />
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                    <span className="text-[9px] font-bold text-gray-300 dark:text-white/10 uppercase tracking-widest mt-3 px-1">{msg.timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                </div>
                            ))}
                            {isTyping && (
                                <div className="bg-gray-50 dark:bg-[#181818] p-4 rounded-md rounded-tl-sm w-14 flex gap-1 justify-center">
                                    <div className="w-1 h-1 bg-black/20 dark:bg-white/30 rounded-full animate-bounce" />
                                    <div className="w-1 h-1 bg-black/20 dark:bg-white/30 rounded-full animate-bounce delay-75" />
                                    <div className="w-1 h-1 bg-black/20 dark:bg-white/30 rounded-full animate-bounce delay-150" />
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Minimal Input Area */}
                        <div className="relative z-10 p-4 mt-auto">
                            <form onSubmit={handleSend} className="relative flex items-center gap-2 bg-gray-50 dark:bg-[#181818] rounded-full p-2 pl-4 transition-all focus-within:ring-1 focus-within:ring-black/5 dark:focus-within:ring-white/10">
                                <button 
                                    type="button" 
                                    onClick={toggleListening}
                                    className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                                        isListening ? 'text-red-500 bg-red-100 dark:bg-red-500/20' : 'text-gray-400 dark:text-white/30 hover:text-black dark:hover:text-white'
                                    }`}
                                >
                                    {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                                </button>

                                <input 
                                    type="text" 
                                    value={input} 
                                    onChange={e => setInput(e.target.value)}
                                    placeholder="Ask LAQY AI..."
                                    className="flex-grow bg-transparent border-none text-black dark:text-white placeholder-gray-400 dark:placeholder-white/20 text-xs font-bold uppercase tracking-wider px-2 h-10 focus:ring-0 outline-none w-full"
                                />

                                <button 
                                    disabled={!input.trim()} 
                                    type="submit" 
                                    className="w-10 h-10 bg-black dark:bg-white text-white dark:text-black rounded-full flex items-center justify-center hover:scale-105 active:scale-95 disabled:opacity-0 disabled:scale-50 transition-all shadow-lg"
                                >
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </form>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ChatWidget;
